import { INamedEntity } from "../../../../Api/AptorApi";
import { SelectOption } from "../../../../Components/Form/Form.types";
import { selectOptionToEntity } from "../../../../Utilities";
import { entityToSelectOption } from '../../../../Utilities';
import { useIntl } from "react-intl";
import { useCallback, useReducer } from "react";

export type ConnectionType = 'process' | 'aspect' | 'complianceUser' | 'tag';


interface IConnectionOptions {
    aspect: SelectOption[];
    process: SelectOption[];
    complianceUser: SelectOption[];
    tag: SelectOption[];
  }
interface IUpdatedReq { 
    id: number, 
    name: string, 
    number: string 
};

interface IType{
    key: ConnectionType;
    name: string;
}

export interface IState {
    companyUnit: number;
    _reqs: IReqConnections[];
    _connections: IConnectionOptions;

    type?: IType;
    connection?: INamedEntity;

    typeOptions: SelectOption[];
    connectionOptions: SelectOption[];

    connectionAddedToReqs: IUpdatedReq[];
    connectionRemovedFromReqs: IUpdatedReq[];
}

export interface IReqConnectionsModel {
    aspects: INamedEntity[];
    processes: INamedEntity[];
    tags: INamedEntity[];
    complianceUsers: INamedEntity[];
    reqs: IReqConnections[];
}

interface IReqConnections {
    id: number;
    aspect: number[];
    process: number[];
    complianceUser: number[];
    tag: number[];
}

type actions = 
    { type: 'setType'; payload: SelectOption } |
    { type: 'loadConnections', payload: IReqConnectionsModel } | 
    { type: 'setConnection', payload?: SelectOption } |
    { type: 'addConnection', payload: { req: IUpdatedReq }} |
    { type: 'removeConnection', payload: { req: IUpdatedReq }} |
    { type: 'resetState', payload: IReqConnectionsModel };

const reducer = (state: IState, action: actions): IState => {
    switch (action.type) {     
        case 'loadConnections': return {
            ...state,
            ...{
                _reqs: action.payload.reqs,
                _connections:{
                    aspect: action.payload.aspects.map(entityToSelectOption),
                    process: action.payload.processes.map(entityToSelectOption),
                    tag: action.payload.tags.map(entityToSelectOption),
                    complianceUser: action.payload.complianceUsers.map(entityToSelectOption),
                }
            }
        }
        case 'resetState': return {
            ...state,
            ...{
                _reqs: action.payload.reqs,
                _connections:{
                    aspect: action.payload.aspects.map(entityToSelectOption),
                    process: action.payload.processes.map(entityToSelectOption),
                    tag: action.payload.tags.map(entityToSelectOption),
                    complianceUser: action.payload.complianceUsers.map(entityToSelectOption),
                },                
                type: undefined,
                connection: undefined,
                connectionOptions: [],
                connectionAddedToReqs: [],
                connectionRemovedFromReqs: [],                
            }
        }
        case 'setType': {
            const key = action.payload?.value as ConnectionType;
            const type = { key, name:action.payload?.label }
            return {
              ...state,
              ...{
                type: type,
                connectionOptions: state._connections[key],
                connection: undefined,
              },
            };
          }
        case 'setConnection': {
            const connection = action.payload ? selectOptionToEntity<number>(action.payload): undefined;
            return {
                ...state,
                ...{
                    connection: connection,
                    connectionAddedToReqs: [],
                    connectionRemovedFromReqs: [],
                }
            }
        }
        case 'addConnection': {
            if(state.type){
                const req = action.payload.req;
                const foundReq = state._reqs.find(r => r.id === req.id);
                const exists = foundReq && foundReq[state.type.key].some(connection => connection === state.connection?.id);
                return {
                    ...state,
                    ...{
                        connectionAddedToReqs: exists
                            ? state.connectionAddedToReqs
                            : [...state.connectionAddedToReqs.filter(l => l.id !== req.id), req],
                        connectionRemovedFromReqs: state.connectionRemovedFromReqs.filter(l => l.id !== req.id)
                    }
                }
            }
            else return state;
        }
        case 'removeConnection':{
            if(state.type) {
                const req = action.payload.req;
                const foundReq = state._reqs.find(r => r.id === req.id);
                const exists = foundReq && foundReq[state.type.key].some(connection => connection === state.connection?.id);
                return {
                    ...state,
                    ...{
                        connectionAddedToReqs: state.connectionAddedToReqs.filter(r => r.id !== action.payload.req.id),
                        connectionRemovedFromReqs: exists
                            ? [...state.connectionRemovedFromReqs.filter(r => r.id !== req.id), req]
                            : state.connectionRemovedFromReqs
                    }
                }
            }
            else return state;
        }     
        default:
      throw new Error();
  }
};

export interface IRequirementConnectionsHook{
    state: IState,
    loadRequirementConnections: (connections: IReqConnectionsModel) => void;
    setType: (selected: SelectOption) => void;
    setConnection: (selected?: SelectOption) => void;
    addRequirementConnection: (req: IUpdatedReq) => void;
    removeRequirementConnection: (req: IUpdatedReq) => void;
    requirementHasConnection: (reqId:number) => boolean;
    resetState: (connections: IReqConnectionsModel) => void;
}

export const useRequirementConnectionUpdateState = (companyUnitId: number): IRequirementConnectionsHook => {
    const {formatMessage} = useIntl();
    const initialState = {
        _reqs: [],
        _connections: { aspect: [], process: [], complianceUser: [], tag:[] },
        companyUnit: companyUnitId,
        connectionAddedToReqs: [],
        connectionRemovedFromReqs: [],
        typeOptions: [
            { label: formatMessage({ id: 'law-portal.manage.update-requirements.type.options.aspect' }), value: 'aspect' },
            { label: formatMessage({ id: 'law-portal.manage.update-requirements.type.options.process' }), value: 'process' },
            {
              label: formatMessage({ id: 'law-portal.manage.update-requirements.type.options.complianceUser' }),
              value: 'complianceUser',
            },
            {
                label: formatMessage({ id: 'law-portal.manage.update-requirements.type.options.tag' }),
                value: 'tag',
              },
          ],
          connectionOptions: []
    };
    const [state, dispatch] = useReducer(reducer, initialState);
    const loadRequirementConnections = useCallback((connections: IReqConnectionsModel) => dispatch({ type: "loadConnections", payload: connections }), []);
    const setType = useCallback((selected: SelectOption) => dispatch({ type: "setType", payload: selected }), []);
    const setConnection = useCallback((selected?: SelectOption) => dispatch({ type: "setConnection", payload: selected }), []);
    const addRequirementConnection =  useCallback((req: IUpdatedReq) => dispatch({ type: "addConnection", payload: { req } }), []);
    const removeRequirementConnection =  useCallback((req: IUpdatedReq) => dispatch({ type: "removeConnection", payload: { req} }), []);
    const resetState =  useCallback((connections: IReqConnectionsModel) => dispatch({ type: "resetState", payload: connections }), []);
    const requirementHasConnection = useCallback((reqId:number) => {
        if(state.connectionAddedToReqs.some(req => req.id === reqId)){
            return true;
        }
        else if(state.connectionRemovedFromReqs.some(req => req.id === reqId)){
            return false;
        }
        
        const initialReqConnections = state._reqs.find(x => x.id === reqId);
        if (initialReqConnections && state.type){
            return initialReqConnections[state.type.key].some(x => x === state.connection?.id);
        }
    
        return false;
    }, [state])

    return {
        state, 
        loadRequirementConnections, 
        setType, 
        setConnection, 
        addRequirementConnection, 
        removeRequirementConnection, 
        requirementHasConnection, 
        resetState
    };
  };